class AriaListbox extends HTMLElement {
  static observedAttributes = ["active"];

  constructor() {
    super();

    this.addEventListener("keydown", (e) => {
      switch (e.code) {
        case "ArrowUp":
          e.preventDefault();
          this._previous();
          break;
        case "ArrowDown":
          e.preventDefault();
          this._next();
          break;
        case "Home":
          e.preventDefault();
          this._first();
          break;
        case "End":
          e.preventDefault();
          this._last();
          break;
        default:
          break;
      }
    });

    // Arrowing moves the tab stop; put it back on the selected option once
    // focus leaves so Tab returns there, not wherever the user stopped
    this.addEventListener("focusout", (e) => {
      if (!this.contains(e.relatedTarget)) this._resetTabStop();
    });

    this.addEventListener("click", (e) => {
      const option = e.target.closest(
        '[role="option"]:not([aria-disabled="true"])',
      );
      if (!option) return;

      this.selected.forEach((el) => el.setAttribute("aria-selected", "false"));
      option.setAttribute("aria-selected", "true");
      this._resetTabStop();
      this._setInputValue(option.dataset.value);
      option.dispatchEvent(
        new CustomEvent("aria-listbox:change", {
          bubbles: true,
          detail: { value: option.dataset.value },
        }),
      );
    });
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "active" && oldValue === null && newValue !== null) {
      this._focus(this._tabStop);
    }
  }

  connectedCallback() {
    this._resetTabStop();
  }

  get active() {
    return this.hasAttribute("active");
  }

  get options() {
    return [
      ...this.querySelectorAll('[role="option"]:not([aria-disabled="true"])'),
    ];
  }

  get selected() {
    return [...this.querySelectorAll('[role="option"][aria-selected="true"]')];
  }

  /** The selected option, or the first option when nothing is selected. */
  get _tabStop() {
    const options = this.options;
    return (
      options.find((el) => el.getAttribute("aria-selected") === "true") ??
      options[0]
    );
  }

  get _currentIndex() {
    return this.options.indexOf(
      document.activeElement?.closest('[role="option"]'),
    );
  }

  _resetTabStop() {
    const tabStop = this._tabStop;
    this.options.forEach((el) => {
      el.tabIndex = el === tabStop ? 0 : -1;
    });
  }

  _setInputValue(value) {
    const input = this.querySelector("[data-input]");

    if (!input) return;

    input.value = value;
  }

  _first() {
    this._focus(this.options[0]);
  }

  _last() {
    const options = this.options;
    this._focus(options[options.length - 1]);
  }

  _next() {
    const options = this.options;
    const currentIndex = this._currentIndex;
    const newIndex = Math.min(currentIndex + 1, options.length - 1);
    if (newIndex === currentIndex) return;

    this._focus(options[newIndex]);
  }

  _previous() {
    const options = this.options;
    const currentIndex = this._currentIndex;
    const newIndex = Math.max(currentIndex - 1, 0);
    if (newIndex === currentIndex) return;

    this._focus(options[newIndex]);
  }

  _focus(el) {
    if (!el) return;
    this.options.forEach((option) => {
      option.tabIndex = option === el ? 0 : -1;
    });
    el.focus();
  }
}

if (!customElements.get("aria-listbox")) {
  customElements.define("aria-listbox", AriaListbox);
}
